import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import moment from 'moment';
import { ReactComponent as Edit } from '../../../../assets/common-assets/edit.svg';
import { lightPalette, theme } from '../../../../theme';
import { classes, Root } from './utils';
import AgGrid from '../../common-components/common-ag-grid/AgGrid';
import { ACCOUNT_INFO } from '../../../../constants/staticContent';
import { getLocalStorageItem } from '../../../../services/localStorageUtils';
import ROLES from '../../../../constants/roles';
import CustomTooltip from '../../utils/CustomTooltip';
import EditManager from './edit-details/EditManager';
import { getManagers } from '../../../../services/billingInfo';
import OrganizationSubscription from './OrganizationSubscription';
import { getGridFilterSortStatus } from '../../../../services/allInvoiceApi';

const BillingInfo = ({
  functionData,
  columnData,
  getRowData,
  adminDetails,
  adminOrganizationName,
  adminData,
  subscriptionDetails,
  getSubscriptionFromOrganizationId,
  getAdminDetailsFromOrganizationId,
  refreshGrid,
  setRefreshGrid,
}) => {
  const loginRole = getLocalStorageItem('role');
  const [isLoading, setIsLoading] = useState(true);
  const [editDetailOpen, setEditDetailOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedManager, setSelectedManager] = useState({});
  const [manager, setManager] = useState([]);
  const [showSubscriptionPopup, setShowSubscriptionPopup] = useState();
  const [activePlanDetails, setActivePlanDetails] = useState();
  const [gridSettingData, setGridSettingData] = useState({});
  const [activeSubscription, setActiveSubscription] = useState();
  const userDetail = useSelector((state) => state.user.user);
  const nameMerge = `${userDetail?.organization?.billing_infos?.[0]?.user?.first_name || ''} ${userDetail?.organization?.billing_infos?.[0]?.user?.last_name || ''}`;
  const userAvatar =
    userDetail?.organization?.billingInfos?.[0]?.user?.avatar || '';
  const phoneNo =
    userDetail?.organization?.billing_infos?.[0]?.user?.phone_no || '';
  const email = userDetail?.organization?.billing_infos?.[0]?.user?.email || '';

  const openEditInfo = () => {
    setEditDetailOpen(true);
  };

  const getManagerList = async () => {
    const res = await getManagers();
    if (res?.data?.response) {
      const managerData = res.data.response.map((elem) => ({
        value: `${elem.first_name} ${elem.last_name}`,
        label: `${elem.first_name} ${elem.last_name}`,
        id: elem.id,
      }));
      setManager(managerData);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const windowWidth = window.innerWidth;
      setShowTooltip(windowWidth >= 600 && windowWidth <= 900);
    };

    window.addEventListener('resize', handleResize);
    handleResize();

    if (userDetail && Object.keys(userDetail).length !== 0) {
      getManagerList();
    }

    return () => window.removeEventListener('resize', handleResize);
  }, [userDetail]);

  const getDisplayName = (displayBox, name) => {
    if (loginRole === ROLES.SUPER_ADMIN || loginRole === ROLES.SUPER_MANAGER) {
      if (displayBox === 'userNameBox') {
        return `${adminDetails[0]?.user.first_name} ${adminDetails[0]?.user.last_name}`;
      }
      if (displayBox === 'organizationNameBox') {
        return `${adminOrganizationName}`;
      }
    }
    return name;
  };

  const getFilterSortData = async () => {
    try {
      const response = await getGridFilterSortStatus(
        'organization-payment-history',
      );
      setGridSettingData(response);
    } catch (err) {
      return err;
    }
  };
  useEffect(() => {
    getFilterSortData();
  }, []);

  useEffect(() => {
    const getActiveSubscription = subscriptionDetails?.subscriptions?.filter(
      (subscription) => subscription.status === 'active',
    );
    getActiveSubscription?.map((activeSubData) =>
      setActiveSubscription(activeSubData),
    );
  }, [subscriptionDetails.subscriptions]);

  return (
    <Root className={classes.root}>
      {isLoading ? (
        <Box className={classes.loaderBox}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Grid container className={classes.accountInfoBillingInfoSecondGrid}>
            <Grid
              item
              xs={12}
              md={5.78}
              className={classes.billingInfoSecondGridBoxes}
            >
              <Box className={classes.accountDetailsBoarderBottom}>
                <Typography variant="h5" className={classes.gridItemMainTitle}>
                  {ACCOUNT_INFO?.BILLING_INFO?.BILLING_ACCOUNT_DETAILS}
                </Typography>
                {loginRole === ROLES.ADMIN &&
                  userDetail?.organization?.billing_infos.length !== 0 && (
                    <Box>
                      <Edit
                        className={classes.changePasswordButton}
                        onClick={openEditInfo}
                      />
                    </Box>
                  )}
              </Box>

              <Backdrop
                className={classes.changePasswordBackdrop}
                open={editDetailOpen}
              >
                <EditManager
                  setEditDetailOpen={setEditDetailOpen}
                  selectedManager={selectedManager}
                  isLoading={isLoading}
                  setIsLoading={setIsLoading}
                  manager={manager}
                  setManager={setManager}
                  setSelectedManager={setSelectedManager}
                  editDetailOpen={editDetailOpen}
                  userDetail={
                    userDetail?.organization?.billing_infos?.[0]?.user
                  }
                />
              </Backdrop>

              <Box className={classes.addressAndPlanDetailBox}>
                {!isLoading &&
                loginRole === ROLES.ADMIN &&
                manager?.length === 0 ? (
                  <Box className={classes.addressAndPlanDetailBox}>
                    <Box className={classes.managerButtonBox}>
                      <Typography
                        className={classes.titleTypo}
                        variant="body1"
                        color={lightPalette.secondaryBlack.main}
                      >
                        No manager available. Please add a manager first.
                      </Typography>
                    </Box>
                  </Box>
                ) : (loginRole === ROLES.ADMIN &&
                    userDetail?.organization?.billing_infos?.length === 0) ||
                  (loginRole === ROLES.SUPER_ADMIN &&
                    adminDetails?.length === 0) ? (
                  <Box className={classes.addressAndPlanDetailBox}>
                    <Box className={classes.managerButtonBox}>
                      <Typography
                        className={classes.titleTypo}
                        variant="body1"
                        color={lightPalette.secondaryBlack.main}
                      >
                        Admin haven&#39;t selected billing info yet.
                      </Typography>
                      {loginRole === ROLES.ADMIN &&
                        userDetail?.organization?.billing_infos?.length ===
                          0 && (
                          <Button
                            variant="contained"
                            className={classes.managerButton}
                            onClick={openEditInfo}
                          >
                            <Typography
                              variant="body1"
                              color={lightPalette.secondaryWhite.main}
                            >
                              {ACCOUNT_INFO?.BILLING_INFO?.SELECT_MANAGER}
                            </Typography>
                          </Button>
                        )}
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    <Box className={classes.userDetailBox}>
                      <Avatar
                        className={classes.avatar}
                        src={userAvatar}
                        alt="User-Avatar"
                      />
                      <Box>
                        <Typography
                          variant="h5"
                          color={lightPalette.darkPurple.main}
                          className={classes.userNameBox}
                        >
                          {getDisplayName('userNameBox', nameMerge)}
                        </Typography>
                        <Typography
                          variant="body1"
                          color={lightPalette.shoeWax.darker}
                          className={classes.organizationNameBox}
                        >
                          {getDisplayName(
                            'organizationNameBox',
                            userDetail?.organization?.name,
                          )}
                        </Typography>
                      </Box>
                    </Box>
                    <Box className={classes.AdminAddress}>
                      <Typography
                        variant="body1"
                        color={lightPalette.shoeWax.darker}
                      >
                        {(loginRole === ROLES.MANAGER ||
                          loginRole === ROLES.MEMBER ||
                          loginRole === ROLES.ADMIN) &&
                          email}
                        {(loginRole === ROLES.SUPER_MANAGER ||
                          loginRole === ROLES.SUPER_ADMIN) &&
                          adminDetails[0]?.user?.email}
                      </Typography>
                      <Typography
                        variant="body1"
                        color={lightPalette.shoeWax.darker}
                      >
                        {(loginRole === ROLES.MANAGER ||
                          loginRole === ROLES.MEMBER ||
                          loginRole === ROLES.ADMIN) &&
                          phoneNo}
                        {(loginRole === ROLES.SUPER_MANAGER ||
                          loginRole === ROLES.SUPER_ADMIN) &&
                          adminDetails[0]?.user?.phone_no}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid
              item
              xs={12}
              md={5.78}
              display="flex"
              flexDirection="column"
              gap="16px"
            >
              <Box className={classes.billingInfoSecondGridFirstBoxes}>
                <Box className={classes.planDetailsGrid}>
                  <Box className={classes.secondGridTitleBoarderBottom}>
                    <Typography
                      variant="h5"
                      className={classes.gridItemMainTitle}
                    >
                      {
                        ACCOUNT_INFO?.BILLING_INFO
                          ?.CURRENT_PLAN_AND_PAYMENT_DETAILS
                      }
                    </Typography>
                    {loginRole === ROLES.SUPER_ADMIN && (
                      <Edit
                        className={classes.changePasswordButton}
                        onClick={() => {
                          setIsLoading(true);
                          getAdminDetailsFromOrganizationId();
                          setIsLoading(false);
                          setShowSubscriptionPopup(true);
                        }}
                      />
                    )}
                    <Backdrop
                      className={classes.changePasswordBackdrop}
                      open={showSubscriptionPopup}
                    >
                      <OrganizationSubscription
                        setShowSubscriptionPopup={setShowSubscriptionPopup}
                        adminData={adminData}
                        subscriptionDetails={subscriptionDetails}
                        activePlanDetails={activePlanDetails}
                        setActivePlanDetails={setActivePlanDetails}
                        getSubscriptionFromOrganizationId={
                          getSubscriptionFromOrganizationId
                        }
                        setRefreshGrid={setRefreshGrid}
                      />
                    </Backdrop>
                  </Box>
                  <Box className={classes.planDetailBox}>
                    <Box
                      className={`${classes.addressAndPlanDetailBox} ${classes.planDetailAndPaymentInfo}`}
                    >
                      <Box className={classes.paymentDetailBox}>
                        <Box>
                          <Typography
                            variant="body1"
                            color={lightPalette.shoeWax.darker}
                            noWrap
                          >
                            {`Current Plan : ${activePlanDetails?.name || ''}`}
                          </Typography>
                          <Typography
                            variant="body1"
                            color={lightPalette.shoeWax.darker}
                            noWrap
                          >
                            {`Plan Credits : ${activePlanDetails?.credits ? activePlanDetails?.credits : ''}`}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="body1"
                            color={lightPalette.shoeWax.darker}
                            noWrap
                          >
                            {`Start Date : ${moment.utc(activeSubscription?.updated_at).local().format('DD-MM-YYYY') || ''}`}
                          </Typography>
                          <Typography
                            variant="body1"
                            color={lightPalette.shoeWax.darker}
                            noWrap
                          >
                            {`End Date : ${moment(activeSubscription?.updated_at).add(activePlanDetails?.expiry, 'days').local().format('DD-MM-YYYY') || ''}`}
                          </Typography>
                        </Box>
                        {/* {showTooltip && (
                        <CustomTooltip title={`${paymentDate} ${paymentTime}`}>
                          <Typography variant="body1" color={lightPalette.shoeWax.darker} noWrap>
                            {ACCOUNT_INFO.BILLING_INFO.PAYMENT_DATE}
                            :
                            {paymentDate}
                            {' '}
                            {paymentTime}
                          </Typography>
                        </CustomTooltip>
                      )} */}
                        {/* {!showTooltip && (
                        <Typography variant="body1" color={lightPalette.shoeWax.darker} noWrap>
                          {ACCOUNT_INFO?.BILLING_INFO?.PAYMENT_DATE}
                          :
                          {paymentDate}
                          {' '}
                          {paymentTime}
                        </Typography>
                      )} */}
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
              <Box className={classes.billingInfoSecondGridFirstBoxes}>
                <Box className={classes.planDetailsGrid}>
                  <Box className={classes.secondGridTitleBoarderBottom}>
                    <Typography
                      variant="h5"
                      className={classes.gridItemMainTitle}
                    >
                      {ACCOUNT_INFO?.BILLING_INFO?.ACCOUNT}
                    </Typography>
                    {loginRole === ROLES.SUPER_ADMIN && (
                      <Edit
                        className={classes.changePasswordButton}
                        onClick={() => {
                          setShowSubscriptionPopup(true);
                        }}
                      />
                    )}
                    <Backdrop
                      className={classes.changePasswordBackdrop}
                      open={showSubscriptionPopup}
                    >
                      <OrganizationSubscription
                        setShowSubscriptionPopup={setShowSubscriptionPopup}
                        adminData={adminData}
                        subscriptionDetails={subscriptionDetails}
                        activePlanDetails={activePlanDetails}
                        setActivePlanDetails={setActivePlanDetails}
                        getSubscriptionFromOrganizationId={
                          getSubscriptionFromOrganizationId
                        }
                        setRefreshGrid={setRefreshGrid}
                      />
                    </Backdrop>
                  </Box>
                  <Box className={classes.paymentDetailBox}>
                    {showTooltip && (
                      <CustomTooltip
                        title={ACCOUNT_INFO?.BILLING_INFO?.SUBSCRIPTION_DETAIL}
                      >
                        <Typography
                          variant="body1"
                          noWrap
                          color={lightPalette.shoeWax.darker}
                        >
                          {ACCOUNT_INFO?.BILLING_INFO?.SUBSCRIPTION_DETAIL}
                        </Typography>
                      </CustomTooltip>
                    )}
                    {!showTooltip && (
                      <Typography
                        variant="body1"
                        noWrap
                        color={lightPalette.shoeWax.darker}
                      >
                        {ACCOUNT_INFO?.BILLING_INFO?.SUBSCRIPTION_DETAIL}
                      </Typography>
                    )}
                    <Typography
                      variant="h6"
                      color={theme.palette.darkPurple.main}
                    >
                      {activePlanDetails?.name === 'Free'
                        ? 'Free'
                        : `₹${activePlanDetails?.price || ''}`}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
          <Grid container className={classes.gridHeader}>
            <Grid item xs={12}>
              <AgGrid
                TitleHeader="Billing & Payment History"
                className={classes.billingHistoryAgGridStyle}
                showFilterField={false}
                columnData={columnData}
                getRowData={getRowData}
                gridSettingData={gridSettingData}
                gridKey="organization-payment-history"
                functionData={functionData}
                refreshGrid={refreshGrid}
                setGridSettingData={setGridSettingData}
              />
            </Grid>
          </Grid>
        </Box>
      )}
    </Root>
  );
};

const functionDataPropTypes = PropTypes.string;
const columnDataPropTypes = PropTypes.arrayOf(PropTypes.object);
const adminDataPropTypes = PropTypes.arrayOf(PropTypes.object);
const subscriptionDetailsPropTypes = PropTypes.arrayOf(PropTypes.object);

BillingInfo.defaultProps = {
  columnData: [],
  functionData: PropTypes.string,
  adminData: [{}],
  subscriptionDetails: [{}],
};

BillingInfo.propTypes = {
  getRowData: PropTypes.func.isRequired,
  getAdminDetailsFromOrganizationId: PropTypes.func.isRequired,
  functionData: functionDataPropTypes,
  columnData: columnDataPropTypes,
  adminDetails: PropTypes.string.isRequired,
  adminData: adminDataPropTypes,
  adminOrganizationName: PropTypes.string.isRequired,
  subscriptionDetails: subscriptionDetailsPropTypes,
  getSubscriptionFromOrganizationId: PropTypes.func.isRequired,
  refreshGrid: PropTypes.bool.isRequired,
  setRefreshGrid: PropTypes.func.isRequired,
};

export default BillingInfo;
